import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { ProfileSettingsView } from "./admin/ProfileSettingsView";
import { UsersView } from "./admin/UsersView";
import { NotificationsView } from "./admin/NotificationsView";
import { SettingsView } from "./admin/SettingsView";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SidebarProvider, Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarTrigger } from "@/components/ui/sidebar";
import frequentHealthLogo from "@/assets/frequent-health-logo.jpeg";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Wrench,
  Activity,
  Dumbbell,
  FileText,
  Users,
  Plus,
  Edit3,
  Trash2,
  Shield,
  Bell,
  User,
  Package,
  Search,
  Target,
  Heart,
  Star,
  MessageSquare,
  LogOut,
  Settings,
  MoreVertical,
  RotateCcw,
  Copy,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import VarietyField from "@/components/variety-field";
import PricingTiersView from "@/components/admin/PricingTiersView";
import ContactSubmissionsView from "./admin/ContactSubmissionsView";
import FAQView from "./admin/FAQView";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ClientProfileView from "./trainer/ClientProfileView";
import { dummyProfiles } from "@/data/dummyData";
import LegalView from "@/components/admin/LegalView";
import WorkoutsView from "./admin/WorkoutsView";
import LessonsView from "@/components/admin/LessonsView";
import type { Tables } from "@/integrations/backend/types";
import { DIFFICULTY, MUSCLE_GROUPS, MOVEMENT_PATTERNS, type Difficulty as DifficultyEnum } from "@/integrations/backend/enums";
import { fetchEquipmentOptions, listEquipment, reactivateEquipment, softDeleteEquipment, createEquipment, updateEquipment } from "@/integrations/backend/equipment";
import { loadExercisesWithRelations, reactivateExercise, softDeleteExercise, upsertExercise, type AdminExerciseVM, type EquipmentOption } from "@/integrations/backend/exercises";
import { toast } from "@/hooks/use-toast";
import { labels, motivationOptions, programExerciseTypeOptions } from "@/lib/onboarding-options";
import { MUSCLE_ACTION_SECTIONS } from "@/lib/exercise-muscle-actions";
import {
  normalizeExerciseTypeRoles,
  primaryExerciseTypeOptions,
  resolveExerciseTypeRoles,
  syncExerciseTypesFromRoles,
  umbrellaExerciseTypeOptions,
  type ExerciseTypeRoles,
} from "@/lib/exercise-type-roles";
import { exerciseTypeCatalog, exerciseTypeLabelById } from "@/lib/exercise-types";
import { listClients, listTrainers, assignTrainer, type AdminClient, type AdminTrainer } from "@/integrations/backend/admin-users";
import { resolveBackendAssetUrl } from "@/integrations/backend/client";
import { listPricingTiers, type PricingTier } from "@/integrations/backend/pricing";
import { listPrograms, createProgram, updateProgram, deleteProgram, duplicateProgram, type Program } from "@/integrations/backend/programs";
import ExerciseCard from "@/components/admin/exercises/ExerciseCard";
import GroupedMuscleActionFilterItems from "@/components/admin/exercises/GroupedMuscleActionFilterItems";
import {
  EXERCISE_DIFFICULTY_OPTIONS,
  matchesExerciseLibraryFilters,
  summarizeFilterValues,
} from "@/lib/exercise-library-filters";
import CatalogSourceFilterSelect from "@/components/admin/CatalogSourceFilterSelect";
import { matchesCatalogSourceFilter, type CatalogSourceFilter } from "@/lib/catalog-source-filter";

type ColumnDefinition = {
  key: string;
  label: string;
};

type FieldDefinition = {
  key: string;
  label: string;
  placeholder?: string;
};

type SimpleItem = { id: string } & Record<string, string>;

type CrudSectionProps = {
  title: string;
  description?: string;
  columns: ColumnDefinition[];
  fields: FieldDefinition[];
  initialItems: SimpleItem[];
};

function CrudSection(props: CrudSectionProps) {
  const { title, description, columns, fields, initialItems } = props;
  const [items, setItems] = useState<SimpleItem[]>(initialItems);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SimpleItem | null>(null);
  const [formState, setFormState] = useState<Record<string, string>>({});
  const [itemToDelete, setItemToDelete] = useState<SimpleItem | null>(null);

  const isEditing = useMemo(() => Boolean(editingItem), [editingItem]);

  const openCreate = () => {
    setEditingItem(null);
    setFormState({});
    setIsDialogOpen(true);
  };

  const openEdit = (item: SimpleItem) => {
    setEditingItem(item);
    setFormState(item);
    setIsDialogOpen(true);
  };

  const handleChange = (key: string, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    if (isEditing && editingItem) {
      setItems((prev) => prev.map((it) => (it.id === editingItem.id ? ({ ...editingItem, ...formState }) : it)));
    } else {
      const newItem = {
        id: String(Date.now()),
        ...formState,
      } as SimpleItem;
      setItems((prev) => [newItem, ...prev]);
    }
    setIsDialogOpen(false);
    setEditingItem(null);
    setFormState({});
  };

  const confirmDelete = (item: SimpleItem) => {
    setItemToDelete(item);
  };

  const handleDelete = () => {
    if (!itemToDelete) return;
    setItems((prev) => prev.filter((it) => it.id !== itemToDelete.id));
    setItemToDelete(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">{title}</h1>
          {description && <p className="text-gray-600">{description}</p>}
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-gradient-to-r from-primary to-lime-hover">
              <Plus className="w-4 h-4 mr-2" />
              Add {title.slice(0, -1)}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white text-black">
            <DialogHeader>
              <DialogTitle>{isEditing ? "Edit" : "Add"} {title.slice(0, -1)}</DialogTitle>
              <DialogDescription>Fill out the fields below and save.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {fields.map((field) => (
                <div key={String(field.key)} className="space-y-2">
                  <Label htmlFor={String(field.key)}>{field.label}</Label>
                  <Input
                    id={String(field.key)}
                    placeholder={field.placeholder}
                    value={String((formState[field.key] ?? "") as string)}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                  />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" className="border-gray-300 text-gray-800 hover:bg-gray-100 hover:text-gray-900" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{title} List</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((col) => (
                    <TableHead key={String(col.key)}>{col.label}</TableHead>
                  ))}
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    {columns.map((col) => (
                      <TableCell key={String(col.key)}>{String(item[col.key] ?? "")}</TableCell>
                    ))}
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(item)}>
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => confirmDelete(item)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={Boolean(itemToDelete)} onOpenChange={(open) => !open && setItemToDelete(null)}>
        <AlertDialogContent className="bg-white text-black">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {title.slice(0, -1)}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The item will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Admin Exercises view replicating trainer Exercise Templates (no "My" tab)
function AdminExercisesView() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("exerciseSearch") ?? "");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedMuscleGroups, setSelectedMuscleGroups] = useState<string[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>([]);
  const [selectedExerciseTypes, setSelectedExerciseTypes] = useState<string[]>([]);
  const [catalogSourceFilter, setCatalogSourceFilter] = useState<CatalogSourceFilter>("all");
  const [showDeleted, setShowDeleted] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [itemToReactivate, setItemToReactivate] = useState<string | null>(null);

  type AdminExercise = AdminExerciseVM;

  const resetAllFilters = () => {
    setSearchTerm("");
    setSelectedCategories([]);
    setSelectedMuscleGroups([]);
    setSelectedEquipment([]);
    setSelectedActions([]);
    setSelectedDifficulties([]);
    setSelectedExerciseTypes([]);
    setCatalogSourceFilter("all");
    setShowDeleted(false);
  };

  const summarize = summarizeFilterValues;

  // (Exercise card media helpers moved to frontend/src/lib/exercise-media.ts)

  const exercisesAdminKey = queryKeys.exercises.adminList;
  const equipmentOptsKey = queryKeys.equipment.options;
  const exercisesQuery = useQuery({
    queryKey: exercisesAdminKey,
    queryFn: loadExercisesWithRelations,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...exercisesAdminKey]),
  });
  const equipmentOptionsQuery = useQuery({
    queryKey: equipmentOptsKey,
    queryFn: fetchEquipmentOptions,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...equipmentOptsKey]),
  });
  const exercisesList = exercisesQuery.data ?? [];
  const equipmentOptions = equipmentOptionsQuery.data ?? [];
  const isLoading = exercisesQuery.isLoading || equipmentOptionsQuery.isLoading;
  const [isSaving, setIsSaving] = useState(false);

  // Dialog state for Add/Edit exercise
  const [exerciseDialogOpen, setExerciseDialogOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState<AdminExercise | null>(null);
  const [exName, setExName] = useState("");
  const [exCategories, setExCategories] = useState<string[]>([]);
  const [exPrimary, setExPrimary] = useState<string[]>([]);
  const [exSecondary, setExSecondary] = useState<string[]>([]);
  const [exEquipment, setExEquipment] = useState<string[]>([]);
  const [exEquipmentSearch, setExEquipmentSearch] = useState("");
  const [exDifficulties, setExDifficulties] = useState<string[]>([]);
  const [exInstructions, setExInstructions] = useState("");
  const [exCompound, setExCompound] = useState(false);
  const [exActions, setExActions] = useState<string[]>([]);
  const [exTrainingCategory, setExTrainingCategory] = useState<string>("");
  const [exPrimaryType, setExPrimaryType] = useState<string>("");
  const [exSecondaryTypes, setExSecondaryTypes] = useState<string[]>([]);
  const [exTips, setExTips] = useState("");
  const [exProgrammingUse, setExProgrammingUse] = useState("");
  const [exImageFile, setExImageFile] = useState<File | null>(null);
  const [exImagePreview, setExImagePreview] = useState<string | null>(null);
  const [exVideoUrl, setExVideoUrl] = useState<string>("");
  const [exAiNotes, setExAiNotes] = useState<string>("");

  const resetExerciseForm = () => {
    setEditingExercise(null);
    setExName("");
    setExCategories([]);
    setExPrimary([]);
    setExSecondary([]);
    setExEquipment([]);
    setExEquipmentSearch("");
    setExDifficulties([]);
    setExInstructions("");
    setExCompound(false);
    setExActions([]);
    setExTrainingCategory("");
    setExPrimaryType("");
    setExSecondaryTypes([]);
    setExTips("");
    setExProgrammingUse("");
    setExImageFile(null);
    setExImagePreview(null);
    setExVideoUrl("");
    setExAiNotes("");
  };

  const openCreateExercise = () => {
    resetExerciseForm();
    setExerciseDialogOpen(true);
  };

  const openEditExercise = (ex: AdminExercise) => {
    setEditingExercise(ex);
    setExName(ex.name);
    setExCategories(ex.categories || []);
    setExPrimary(ex.primary_muscles);
    setExSecondary(ex.secondary_muscles);
    setExEquipment(ex.equipment);
    setExDifficulties((ex as any).difficulties || []);
    setExInstructions(ex.instructions);
    setExCompound(ex.is_compound);
    setExActions(ex.muscle_actions || []);
    const roles = resolveExerciseTypeRoles(ex.exercise_type_roles, ex.exercise_types || []);
    setExTrainingCategory(roles.training_category ?? "");
    setExPrimaryType(roles.primary ?? "");
    setExSecondaryTypes(roles.secondary ?? []);
    setExTips(ex.tips || "");
    setExProgrammingUse(ex.programming_use || "");
    setExImageFile(null);
    // Show existing image as preview if available
    setExImagePreview((ex as any).image_url || null);
    setExVideoUrl((ex as any).video_url || "");
    setExAiNotes((ex as any).ai_notes || "");
    setExerciseDialogOpen(true);
  };
  
  const refreshExercises = async () => {
    await queryClient.invalidateQueries({ queryKey: exercisesAdminKey });
    await queryClient.invalidateQueries({ queryKey: equipmentOptsKey });
  };

  // Cleanup object URL when component unmounts or preview changes
  useEffect(() => {
    return () => {
      if (exImagePreview && exImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(exImagePreview);
      }
    };
  }, [exImagePreview]);

  const buildExerciseTypeRoles = (): ExerciseTypeRoles =>
    normalizeExerciseTypeRoles({
      training_category: exTrainingCategory || undefined,
      primary: exPrimaryType || undefined,
      secondary: exSecondaryTypes,
    });

  const formatDifficultySummary = (values: string[]) => {
    if (values.length === 0) return "Select difficulties";
    const order = DIFFICULTY as unknown as string[];
    const sorted = [...values].sort((a, b) => order.indexOf(a) - order.indexOf(b));
    if (sorted.length === 1) return sorted[0];
    return `${sorted[0]} → ${sorted[sorted.length - 1]}`;
  };

  const saveExercise = async () => {
    if (!exName.trim() || exCategories.length === 0 || exDifficulties.length === 0) return;
    setIsSaving(true);
    try {
      const exerciseTypeRoles = buildExerciseTypeRoles();
      await upsertExercise({
        id: editingExercise?.id,
        name: exName.trim(),
        instructions: exInstructions.trim(),
        tips: exTips.trim() || undefined,
        programming_use: exProgrammingUse.trim() || undefined,
        ai_notes: exAiNotes.trim() || undefined,
        is_compound: exCompound,
        categories: exCategories as any,
        primary_muscles: exPrimary as any,
        secondary_muscles: exSecondary as any,
        muscle_actions: exActions as any,
        equipmentNames: exEquipment,
        difficulties: exDifficulties as DifficultyEnum[],
        video_url: exVideoUrl.trim() || undefined,
        exercise_type_roles: exerciseTypeRoles,
        exercise_types: syncExerciseTypesFromRoles(exerciseTypeRoles),
      }, equipmentOptions, exImageFile || undefined);
      await refreshExercises();
      setExerciseDialogOpen(false);
      resetExerciseForm();
      toast({ title: editingExercise ? "Exercise updated" : "Exercise created" });
    } catch (err: any) {
      toast({ title: "Save failed", description: String(err?.message || err) });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await softDeleteExercise(itemToDelete);
      await refreshExercises();
    } catch (err: any) {
      toast({ title: "Delete failed", description: String(err?.message || err) });
    } finally {
      setItemToDelete(null);
    }
  };

  const handleReactivate = async () => {
    if (!itemToReactivate) return;
    try {
      await reactivateExercise(itemToReactivate);
      await refreshExercises();
    } catch (err: any) {
      toast({ title: "Reactivate failed", description: String(err?.message || err) });
    } finally {
      setItemToReactivate(null);
    }
  };

  const generateUniqueName = (baseName: string, existingNames: string[]): string => {
    // Remove any existing number suffix pattern like " (1)", " (2)", etc.
    const baseMatch = baseName.match(/^(.+?)(?:\s+\(\d+\))?$/);
    const cleanBase = baseMatch ? baseMatch[1] : baseName;
    
    // Check if the base name itself is unique
    if (!existingNames.includes(cleanBase)) {
      return cleanBase;
    }
    
    // Try appending numbers until we find a unique name
    let counter = 1;
    let candidate = `${cleanBase} (${counter})`;
    while (existingNames.includes(candidate)) {
      counter++;
      candidate = `${cleanBase} (${counter})`;
    }
    return candidate;
  };

  const handleDuplicate = async (exercise: AdminExercise) => {
    try {
      // Get all existing exercise names to check for uniqueness
      const existingNames = exercisesList.map(ex => ex.name);
      
      // Generate a unique name
      const uniqueName = generateUniqueName(exercise.name, existingNames);
      
      // Create the duplicate on the backend
      const roles = resolveExerciseTypeRoles(exercise.exercise_type_roles, exercise.exercise_types || []);
      await upsertExercise({
        // Don't include id so it creates a new exercise
        name: uniqueName,
        instructions: exercise.instructions,
        tips: exercise.tips || undefined,
        programming_use: exercise.programming_use || undefined,
        ai_notes: (exercise as any).ai_notes || undefined,
        is_compound: exercise.is_compound,
        categories: exercise.categories as any,
        primary_muscles: exercise.primary_muscles as any,
        secondary_muscles: exercise.secondary_muscles as any,
        muscle_actions: exercise.muscle_actions as any,
        equipmentNames: exercise.equipment,
        difficulties: (exercise as any).difficulties || [],
        video_url: exercise.video_url || undefined,
        exercise_type_roles: roles,
        exercise_types: syncExerciseTypesFromRoles(roles),
      }, equipmentOptions);
      
      // Refresh the exercises list to show the new duplicate
      await refreshExercises();
      toast({ title: "Exercise duplicated", description: `Created "${uniqueName}"` });
    } catch (err: any) {
      toast({ title: "Duplicate failed", description: String(err?.message || err) });
    }
  };

  const categories = MOVEMENT_PATTERNS as unknown as string[];
  const muscleGroups = MUSCLE_GROUPS as unknown as string[];
  const equipmentNames = equipmentOptions.map(e => e.name);
  const filteredEquipmentNames = equipmentNames.filter((name) =>
    name.toLowerCase().includes(exEquipmentSearch.trim().toLowerCase())
  );
  const umbrellaTypeOptions = umbrellaExerciseTypeOptions();
  const primaryTypeOptions = primaryExerciseTypeOptions();
  const exerciseTypeFilterOptions = exerciseTypeCatalog.map((t) => ({ id: t.id, label: t.label }));

  const filteredExercises = exercisesList.filter((exercise) => {
    const matchesDeletedStatus = showDeleted ? exercise.is_deleted : !exercise.is_deleted;
    const matchesCatalogSource = matchesCatalogSourceFilter(exercise.ai_generated, catalogSourceFilter);

    return (
      matchesDeletedStatus &&
      matchesCatalogSource &&
      matchesExerciseLibraryFilters(exercise, {
        searchQuery: searchTerm,
        movementPatterns: selectedCategories,
        muscleGroups: selectedMuscleGroups,
        equipment: selectedEquipment,
        muscleActions: selectedActions,
        difficulties: selectedDifficulties,
        exerciseTypes: selectedExerciseTypes,
      })
    );
  });

  // (Difficulty colors + category icon logic are shared in ExerciseCard)

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Exercises</h1>
          <p className="text-gray-600">General exercise templates for all trainers.</p>
        </div>
        <Button onClick={openCreateExercise}>
          Create New Exercise
        </Button>
      </div>

      <div className="flex items-center gap-4 border-b border-gray-200 pb-2">
        <button
          onClick={() => setShowDeleted(false)}
          className={`px-4 py-2 font-medium transition-colors ${
            !showDeleted
              ? "text-primary border-b-2 border-primary"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Active Exercises
        </button>
        <button
          onClick={() => setShowDeleted(true)}
          className={`px-4 py-2 font-medium transition-colors ${
            showDeleted
              ? "text-primary border-b-2 border-primary"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Deleted Exercises
        </button>
      </div>

      <div className="flex flex-col space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search exercises..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-gray-300 bg-white text-gray-900 placeholder:text-gray-500 focus-visible:ring-gray-400"
          />
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={resetAllFilters}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset Filters
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <CatalogSourceFilterSelect value={catalogSourceFilter} onChange={setCatalogSourceFilter} />

          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-700">Movement Pattern</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                  <span>{summarize(selectedCategories, "Select patterns")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white text-black">
                {(categories as string[]).map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c}
                    checked={selectedCategories.includes(c)}
                    onCheckedChange={(v) => {
                      setSelectedCategories((prev) => v ? [...prev, c] : prev.filter(x => x !== c));
                    }}
                    onSelect={(e) => e.preventDefault()}
                    className="data-[state=checked]:bg-gray-100 data-[state=checked]:font-semibold"
                  >
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); setSelectedCategories([]); }}
                  className="text-gray-700"
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-700">Muscle Group</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                  <span>{summarize(selectedMuscleGroups, "Select groups")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white text-black">
                {(muscleGroups as string[]).map((g) => (
                  <DropdownMenuCheckboxItem
                    key={g}
                    checked={selectedMuscleGroups.includes(g)}
                    onCheckedChange={(v) => {
                      setSelectedMuscleGroups((prev) => v ? [...prev, g] : prev.filter(x => x !== g));
                    }}
                    onSelect={(e) => e.preventDefault()}
                    className="data-[state=checked]:bg-gray-100 data-[state=checked]:font-semibold"
                  >
                    {g}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); setSelectedMuscleGroups([]); }}
                  className="text-gray-700"
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-700">Equipment</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                  <span>{summarize(selectedEquipment, "Select equipment")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white text-black">
                {equipmentNames.map((eq) => (
                  <DropdownMenuCheckboxItem
                    key={eq}
                    checked={selectedEquipment.includes(eq)}
                    onCheckedChange={(v) => {
                      setSelectedEquipment((prev) => v ? [...prev, eq] : prev.filter(x => x !== eq));
                    }}
                    onSelect={(e) => e.preventDefault()}
                    className="data-[state=checked]:bg-gray-100 data-[state=checked]:font-semibold"
                  >
                    {eq}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); setSelectedEquipment([]); }}
                  className="text-gray-700"
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-700">Muscle Action</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                  <span>{summarize(selectedActions, "Select actions")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="bg-white text-black max-h-[400px] overflow-y-auto w-[min(100vw-2rem,28rem)]"
              >
                <GroupedMuscleActionFilterItems
                  selected={selectedActions}
                  onToggle={(action, checked) => {
                    setSelectedActions((prev) =>
                      checked ? [...prev, action] : prev.filter((x) => x !== action)
                    );
                  }}
                  onClear={() => setSelectedActions([])}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-700">Difficulty</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                  <span>{summarize(selectedDifficulties, "Select difficulties")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white text-black">
                {EXERCISE_DIFFICULTY_OPTIONS.map((level) => (
                  <DropdownMenuCheckboxItem
                    key={level}
                    checked={selectedDifficulties.includes(level)}
                    onCheckedChange={(v) => {
                      setSelectedDifficulties((prev) =>
                        v ? [...prev, level] : prev.filter((x) => x !== level)
                      );
                    }}
                    onSelect={(e) => e.preventDefault()}
                    className="data-[state=checked]:bg-gray-100 data-[state=checked]:font-semibold"
                  >
                    {level}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setSelectedDifficulties([]);
                  }}
                  className="text-gray-700"
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-700">Exercise Type</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                  <span>{summarize(selectedExerciseTypes.map((id) => exerciseTypeLabelById[id] ?? id), "Select types")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white text-black max-h-[400px] overflow-y-auto w-[min(100vw-2rem,24rem)]">
                {exerciseTypeFilterOptions.map((type) => (
                  <DropdownMenuCheckboxItem
                    key={type.id}
                    checked={selectedExerciseTypes.includes(type.id)}
                    onCheckedChange={(v) => {
                      setSelectedExerciseTypes((prev) =>
                        v ? [...prev, type.id] : prev.filter((x) => x !== type.id)
                      );
                    }}
                    onSelect={(e) => e.preventDefault()}
                    className="data-[state=checked]:bg-gray-100 data-[state=checked]:font-semibold"
                  >
                    {type.label}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setSelectedExerciseTypes([]);
                  }}
                  className="text-gray-700"
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredExercises.map((exercise) => (
          <ExerciseCard
            key={exercise.id}
            exercise={exercise}
            onClick={() => openEditExercise(exercise)}
            topRight={
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-white/90 hover:bg-white rounded-full h-8 w-8 shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="w-4 h-4 text-gray-700" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white text-black z-50">
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    openEditExercise(exercise);
                  }}>
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  {!showDeleted ? (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(exercise);
                      }}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate
                    </DropdownMenuItem>
                  ) : null}
                  {!showDeleted ? (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setItemToDelete(exercise.id);
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setItemToReactivate(exercise.id);
                      }}
                      className="text-green-600 focus:text-green-600"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reactivate
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            }
          />
        ))}
      </div>

      {/* Add/Edit Exercise Dialog */}
      <Dialog open={exerciseDialogOpen} onOpenChange={(open) => { setExerciseDialogOpen(open); if (!open) resetExerciseForm(); }}>
        <DialogScrollContent className="bg-white text-black sm:max-w-[900px]">
          <DialogHeader className="shrink-0">
            <DialogTitle>{editingExercise ? "Edit Exercise" : "Create Exercise"}</DialogTitle>
            <DialogDescription>Define the exercise properties and save.</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4 py-1 pr-1">
            <div className="space-y-2">
              <Label htmlFor="ex-name">Name</Label>
              <Input id="ex-name" value={exName} onChange={(e) => setExName(e.target.value)} placeholder="e.g., Barbell Row" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Movement Types</Label>
                <p className="text-xs text-muted-foreground">Select 1 primary; a second is optional.</p>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                      <span>{summarize(exCategories, "Select movement types")}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-white text-black max-h-[300px] overflow-y-auto">
                    {(categories as string[]).map((c) => {
                      const atCap = exCategories.length >= 2 && !exCategories.includes(c);
                      return (
                      <DropdownMenuCheckboxItem
                        key={c}
                        checked={exCategories.includes(c)}
                        disabled={atCap}
                        onCheckedChange={(v) => setExCategories((prev) => v ? [...prev, c].slice(0, 2) : prev.filter(x => x !== c))}
                        onSelect={(e) => e.preventDefault()}
                        className="data-[state=checked]:bg-gray-100 data-[state=checked]:font-semibold"
                      >
                        {c}
                      </DropdownMenuCheckboxItem>
                      );
                    })}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setExCategories([]); }}>Clear</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="space-y-2">
                <Label>Difficulties</Label>
                <p className="text-xs text-muted-foreground">Multi-select shows as a range (e.g. Beginner → Intermediate).</p>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                      <span>{formatDifficultySummary(exDifficulties)}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-white text-black max-h-[300px] overflow-y-auto">
                    {(DIFFICULTY as unknown as string[]).map((level) => (
                      <DropdownMenuCheckboxItem
                        key={level}
                        checked={exDifficulties.includes(level)}
                        onCheckedChange={(v) => setExDifficulties((prev) => v ? [...prev, level] : prev.filter(x => x !== level))}
                        onSelect={(e) => e.preventDefault()}
                        className="data-[state=checked]:bg-gray-100 data-[state=checked]:font-semibold"
                      >
                        {level}
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setExDifficulties([]); }}>Clear</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Training Category</Label>
                <Select value={exTrainingCategory || "__none__"} onValueChange={(v) => setExTrainingCategory(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="border-gray-300 text-gray-800">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-black max-h-[300px]">
                    <SelectItem value="__none__">None</SelectItem>
                    {umbrellaTypeOptions.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Primary Exercise Type</Label>
                <Select value={exPrimaryType || "__none__"} onValueChange={(v) => setExPrimaryType(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="border-gray-300 text-gray-800">
                    <SelectValue placeholder="Select primary type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-black max-h-[300px]">
                    <SelectItem value="__none__">None</SelectItem>
                    {primaryTypeOptions.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Secondary Exercise Types</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                      <span>{summarize(exSecondaryTypes.map((id) => exerciseTypeLabelById[id] || id), "Select secondary types")}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-white text-black max-h-[300px] overflow-y-auto">
                    {primaryTypeOptions
                      .filter((t) => t.id !== exPrimaryType)
                      .map((t) => (
                        <DropdownMenuCheckboxItem
                          key={t.id}
                          checked={exSecondaryTypes.includes(t.id)}
                          onCheckedChange={(v) =>
                            setExSecondaryTypes((prev) =>
                              v ? [...prev, t.id] : prev.filter((x) => x !== t.id)
                            )
                          }
                          onSelect={(e) => e.preventDefault()}
                          className="data-[state=checked]:bg-gray-100 data-[state=checked]:font-semibold"
                        >
                          {t.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setExSecondaryTypes([]); }}>Clear</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Muscles</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                      <span>{summarize(exPrimary, "Select primary muscles")}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-white text-black max-h-[300px] overflow-y-auto">
                    {(muscleGroups as string[]).map((m) => (
                      <DropdownMenuCheckboxItem
                        key={m}
                        checked={exPrimary.includes(m)}
                        onCheckedChange={(v) => setExPrimary((prev) => v ? [...prev, m] : prev.filter(x => x !== m))}
                        onSelect={(e) => e.preventDefault()}
                        className="data-[state=checked]:bg-gray-100 data-[state=checked]:font-semibold"
                      >
                        {m}
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setExPrimary([]); }}>Clear</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="space-y-2">
                <Label>Secondary Muscles</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                      <span>{summarize(exSecondary, "Select secondary muscles")}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-white text-black max-h-[300px] overflow-y-auto">
                    {(muscleGroups as string[]).map((m) => (
                      <DropdownMenuCheckboxItem
                        key={m}
                        checked={exSecondary.includes(m)}
                        onCheckedChange={(v) => setExSecondary((prev) => v ? [...prev, m] : prev.filter(x => x !== m))}
                        onSelect={(e) => e.preventDefault()}
                        className="data-[state=checked]:bg-gray-100 data-[state=checked]:font-semibold"
                      >
                        {m}
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setExSecondary([]); }}>Clear</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Equipment</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                    <span>{summarize(exEquipment, "Select equipment")}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-white text-black max-h-[300px] overflow-y-auto">
                  <div className="sticky top-0 z-10 bg-white px-1 pb-2">
                    <Input
                      value={exEquipmentSearch}
                      onChange={(e) => setExEquipmentSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder="Search equipment..."
                      className="h-9 bg-white"
                    />
                  </div>
                  {filteredEquipmentNames.length === 0 && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No equipment matches your search.
                    </div>
                  )}
                  {filteredEquipmentNames.map((eq) => (
                    <DropdownMenuCheckboxItem
                      key={eq}
                      checked={exEquipment.includes(eq)}
                      onCheckedChange={(v) => setExEquipment((prev) => v ? [...prev, eq] : prev.filter(x => x !== eq))}
                      onSelect={(e) => e.preventDefault()}
                      className="data-[state=checked]:bg-gray-100 data-[state=checked]:font-semibold"
                    >
                      {eq}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setExEquipment([]); }}>Clear</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="space-y-2">
              <Label>Muscle Actions</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="justify-between w-full border-gray-300 text-gray-800 hover:bg-gray-100">
                    <span>{summarize(exActions, "Select muscle actions")}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-white text-black max-h-[400px] overflow-y-auto w-[min(100vw-2rem,28rem)]">
                  {MUSCLE_ACTION_SECTIONS.map((section) => (
                    <div key={section.id}>
                      <DropdownMenuLabel className="text-xs text-muted-foreground">{section.label}</DropdownMenuLabel>
                      {section.actions.map((a) => (
                        <DropdownMenuCheckboxItem
                          key={a}
                          checked={exActions.includes(a)}
                          onCheckedChange={(v) => setExActions((prev) => v ? [...prev, a] : prev.filter(x => x !== a))}
                          onSelect={(e) => e.preventDefault()}
                          className="data-[state=checked]:bg-gray-100 data-[state=checked]:font-semibold"
                        >
                          {a}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </div>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setExActions([]); }}>Clear</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox id="ex-comp" checked={exCompound} onCheckedChange={(v) => setExCompound(Boolean(v))} />
              <Label htmlFor="ex-comp" className="cursor-pointer">Compound movement</Label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ex-video">Video URL</Label>
                <Input id="ex-video" value={exVideoUrl} onChange={(e) => setExVideoUrl(e.target.value)} placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <Label>Image</Label>
                <Input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) { 
                    // Cleanup old preview URL if it was a blob URL
                    if (exImagePreview && exImagePreview.startsWith('blob:')) {
                      URL.revokeObjectURL(exImagePreview);
                    }
                    setExImageFile(null);
                    setExImagePreview(null);
                    return; 
                  }
                  const lowerName = file.name.toLowerCase();
                  const isHeic = file.type === "image/heic" || file.type === "image/heif" || lowerName.endsWith(".heic") || lowerName.endsWith(".heif");
                  if (isHeic) {
                    toast({
                      title: "Unsupported image format",
                      description: "HEIC/HEIF images aren’t supported in the browser. Please upload a JPG, PNG, or WebP.",
                      variant: "destructive" as any,
                    });
                    e.target.value = "";
                    // Cleanup old preview URL if it was a blob URL
                    if (exImagePreview && exImagePreview.startsWith('blob:')) {
                      URL.revokeObjectURL(exImagePreview);
                    }
                    setExImageFile(null);
                    setExImagePreview(null);
                    return;
                  }
                  // Cleanup old preview URL if it was a blob URL
                  if (exImagePreview && exImagePreview.startsWith('blob:')) {
                    URL.revokeObjectURL(exImagePreview);
                  }
                  setExImageFile(file);
                  const url = URL.createObjectURL(file);
                  setExImagePreview(url);
                }} />
                {exImagePreview && (
                  <div className="mt-2">
                    <img src={exImagePreview} alt="Exercise" className="h-32 w-full object-cover rounded-md border" />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ex-instr">Instructions</Label>
              <Textarea id="ex-instr" value={exInstructions} onChange={(e) => setExInstructions(e.target.value)} placeholder="How to perform the exercise..." className="min-h-[100px]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ex-tips">Tips</Label>
              <Textarea
                id="ex-tips"
                value={exTips}
                onChange={(e) => setExTips(e.target.value)}
                placeholder="Practical form cues for clients..."
                className="min-h-[60px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ex-programming-use">Programming Use</Label>
              <Textarea
                id="ex-programming-use"
                value={exProgrammingUse}
                onChange={(e) => setExProgrammingUse(e.target.value)}
                placeholder="When to use, progressions/regressions, pairing suggestions..."
                className="min-h-[80px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ex-ai-notes">AI Notes</Label>
              <Textarea
                id="ex-ai-notes"
                value={exAiNotes}
                onChange={(e) => setExAiNotes(e.target.value)}
                placeholder="Optional notes for AI workout generation (e.g., cues, when to use, progressions)..."
                className="min-h-[80px]"
              />
            </div>
          </DialogBody>
          <DialogFooter className="shrink-0">
            <Button variant="outline" className="border-gray-300 text-gray-800 hover:bg-gray-100 hover:text-gray-900" onClick={() => setExerciseDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveExercise} disabled={isSaving || !exName.trim() || exCategories.length === 0 || exDifficulties.length === 0}>Save</Button>
          </DialogFooter>
        </DialogScrollContent>
      </Dialog>

      {filteredExercises.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No exercises found matching your criteria.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              setSearchTerm("");
              setSelectedCategories([]);
              setSelectedMuscleGroups([]);
              setSelectedEquipment([]);
              setSelectedActions([]);
              setCatalogSourceFilter("all");
            }}
          >
            Clear All Filters
          </Button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={Boolean(itemToDelete)} onOpenChange={(open) => !open && setItemToDelete(null)}>
        <AlertDialogContent className="bg-white text-black">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Exercise?</AlertDialogTitle>
            <AlertDialogDescription>
              This will soft-delete the exercise. You can reactivate it later from the Deleted Exercises tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reactivate Confirmation Dialog */}
      <AlertDialog open={Boolean(itemToReactivate)} onOpenChange={(open) => !open && setItemToReactivate(null)}>
        <AlertDialogContent className="bg-white text-black">
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate Exercise?</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore the exercise and make it available again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReactivate} className="bg-green-600 text-white hover:bg-green-700">
              Reactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Admin Clients view mirroring trainer Clients with trainer assignment
function AdminClientsView({ 
  workoutAssignmentContext, 
  setWorkoutAssignmentContext,
  setActiveTab,
  viewingClientId,
  setViewingClientId,
  clientProfileTab,
  setClientProfileTab,
  clientWorkoutView,
  setClientWorkoutView
}: { 
  workoutAssignmentContext: { clientId: string; assignmentDate: Date | null; backToClientId: string; assignedWorkoutId?: string } | null;
  setWorkoutAssignmentContext: React.Dispatch<React.SetStateAction<{ clientId: string; assignmentDate: Date | null; backToClientId: string; assignedWorkoutId?: string } | null>>;
  setActiveTab: React.Dispatch<React.SetStateAction<"clients" | "profile" | "equipment" | "exercises" | "workouts" | "programs" | "lessons" | "notifications" | "prompts" | "users" | "contacts" | "legal" | "pricing" | "faqs" | "settings">>;
  viewingClientId: string | null;
  setViewingClientId: React.Dispatch<React.SetStateAction<string | null>>;
  clientProfileTab: string;
  setClientProfileTab: React.Dispatch<React.SetStateAction<string>>;
  clientWorkoutView: "history" | "scheduled";
  setClientWorkoutView: React.Dispatch<React.SetStateAction<"history" | "scheduled">>;
}) {
  const queryClient = useQueryClient();
  const clientsQueryKey = queryKeys.admin.clients.all;
  const trainersQueryKey = queryKeys.admin.trainers;
  const pricingQueryKey = queryKeys.pricingTiers.all;

  const clientsQuery = useQuery({
    queryKey: clientsQueryKey,
    queryFn: listClients,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...clientsQueryKey]),
  });
  const trainersQuery = useQuery({
    queryKey: trainersQueryKey,
    queryFn: listTrainers,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...trainersQueryKey]),
  });
  const pricingTiersQuery = useQuery({
    queryKey: pricingQueryKey,
    queryFn: listPricingTiers,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...pricingQueryKey]),
  });

  const clients = clientsQuery.data ?? [];
  const trainerOptions = trainersQuery.data ?? [];
  const pricingTiers = pricingTiersQuery.data ?? [];
  const loading = clientsQuery.isLoading || trainersQuery.isLoading || pricingTiersQuery.isLoading;

  useEffect(() => {
    const err = clientsQuery.error || trainersQuery.error || pricingTiersQuery.error;
    if (err) {
      console.error("Error fetching clients data:", err);
      toast({
        title: "Error loading clients",
        description: (err as Error)?.message ?? String(err),
        variant: "destructive",
      });
    }
  }, [clientsQuery.error, trainersQuery.error, pricingTiersQuery.error]);

  const NONE_TRAINER_VALUE = "none";
  const [searchTerm, setSearchTerm] = useState("");
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedTrainerId, setSelectedTrainerId] = useState<string>(NONE_TRAINER_VALUE);
  const [trainerFilter, setTrainerFilter] = useState<"all" | "unassigned" | string>("all");
  const [planFilter, setPlanFilter] = useState<"all" | string>("all");

  const planOptions = useMemo(() => {
    const tierNames = pricingTiers.map(t => t.name);
    const clientPlans = clients.map(c => c.pricing_tier).filter(Boolean) as string[];
    return Array.from(new Set([...tierNames, ...clientPlans]));
  }, [pricingTiers, clients]);

  const filteredClients = clients
    .filter(c => {
      const firstName = c.first_name?.toLowerCase() || "";
      const lastName = c.last_name?.toLowerCase() || "";
      const searchLower = searchTerm.toLowerCase();
      return firstName.includes(searchLower) || lastName.includes(searchLower);
    })
    .filter(c => {
      if (trainerFilter === "all") return true;
      if (trainerFilter === "unassigned") return c.trainer == null;
      return c.trainer?.id === trainerFilter;
    })
    .filter(c => (planFilter === "all" ? true : c.pricing_tier === planFilter));

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const openAssign = (clientId: string) => {
    setSelectedClientId(clientId);
    const client = clients.find(c => c.id === clientId);
    setSelectedTrainerId(client?.trainer?.id ?? NONE_TRAINER_VALUE);
    setAssignDialogOpen(true);
  };

  const saveAssignment = async () => {
    if (!selectedClientId) return;
    
    const clientId = selectedClientId;
    const trainerId = selectedTrainerId === NONE_TRAINER_VALUE ? null : selectedTrainerId;
    const previousTrainer = clients.find((c) => c.id === clientId)?.trainer ?? null;

    const nextTrainer =
      trainerId == null
        ? null
        : trainerOptions.find((t) => t.id === trainerId) ?? {
            id: trainerId,
            name: "Trainer",
            initials: "TR",
            avatarUrl: "",
          };

    // Optimistic UX: close immediately and update the list right away.
    setAssignDialogOpen(false);
    setSelectedClientId(null);
    setSelectedTrainerId(NONE_TRAINER_VALUE);

    queryClient.setQueryData<AdminClient[]>(clientsQueryKey, (prev = []) =>
      prev.map((c) => (c.id === clientId ? { ...c, trainer: nextTrainer } : c))
    );

    toast({
      title: trainerId ? "Trainer assigned" : "Trainer unassigned",
      description: trainerId ? "Assignment is being saved…" : "Unassignment is being saved…",
    });

    // Persist in the background. If it fails, revert and notify.
    assignTrainer(clientId, trainerId)
      .then(async () => {
        await queryClient.invalidateQueries({ queryKey: clientsQueryKey });
      })
      .catch((error: any) => {
        console.error("Error assigning trainer:", error);
        queryClient.setQueryData<AdminClient[]>(clientsQueryKey, (prev = []) =>
          prev.map((c) => (c.id === clientId ? { ...c, trainer: previousTrainer } : c))
        );
        toast({
          title: "Error assigning trainer",
          description: error?.message ?? String(error),
          variant: "destructive",
        });
      });
  };

  // Don't render ClientProfileView if workoutAssignmentContext exists
  // The parent will render WorkoutsView instead
  if (workoutAssignmentContext) {
    return null;
  }

  if (viewingClientId) {
    return (
      <div className="space-y-4">
        <ClientProfileView 
          clientId={viewingClientId} 
          onBack={() => setViewingClientId(null)}
          initialTab={clientProfileTab}
          initialWorkoutView={clientWorkoutView}
          onAssignWorkout={(clientId, assignmentDate, assignedWorkoutId) => {
            setWorkoutAssignmentContext({
              clientId,
              assignmentDate,
              backToClientId: viewingClientId!,
              assignedWorkoutId
            });
            // Keep "clients" tab active when assigning from clients view
            // Don't change activeTab - it should stay as "clients"
            // Store the client view state so we can restore it
            localStorage.setItem("admin-viewing-client-id", viewingClientId!);
            localStorage.setItem("admin-client-profile-tab", clientProfileTab);
            localStorage.setItem("admin-client-workout-view", clientWorkoutView);
          }}
          onEditWorkout={(assignedWorkoutId, clientId, assignmentDate) => {
            setWorkoutAssignmentContext({
              clientId,
              assignmentDate,
              backToClientId: viewingClientId!,
              assignedWorkoutId
            });
            // Keep "clients" tab active when editing from clients view
            // Don't change activeTab - it should stay as "clients"
            localStorage.setItem("admin-viewing-client-id", viewingClientId!);
            localStorage.setItem("admin-client-profile-tab", clientProfileTab);
            localStorage.setItem("admin-client-workout-view", clientWorkoutView);
          }}
          onDeleteWorkout={(assignmentId) => {
            // TODO: Implement actual delete API call
            console.log("Delete workout assignment:", assignmentId);
            // For now, just show a toast
            toast({ title: "Workout deleted", description: "The workout has been removed." });
          }}
        />
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="bg-white text-black">
            <DialogHeader>
              <DialogTitle>Assign Trainer</DialogTitle>
              <DialogDescription>Select a trainer or admin to assign.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Trainer</Label>
              <Select value={selectedTrainerId} onValueChange={setSelectedTrainerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a trainer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_TRAINER_VALUE}>None (Unassign)</SelectItem>
                  {trainerOptions.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" className="border-gray-300 text-gray-800 hover:bg-gray-100 hover:text-gray-900" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
              <Button onClick={saveAssignment}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Clients</h1>
          <p className="text-gray-600">Manage clients and assign trainers.</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="mb-6 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search clients..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Filter by Trainer</Label>
                <Select value={trainerFilter} onValueChange={(v) => setTrainerFilter(v as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Trainers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Trainers</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {trainerOptions.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Filter by Plan</Label>
                <Select value={planFilter} onValueChange={(v) => setPlanFilter(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Plans" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    {planOptions.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading clients...</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {filteredClients.map((client) => {
                  const firstName = client.first_name || "";
                  const lastName = client.last_name || "";
                  const fullName = `${firstName} ${lastName}`.trim() || "Unknown";
                  const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase() || "??";
                  const clientAvatarSrc = resolveBackendAssetUrl(client.avatar_url) || undefined;
                  
                  return (
                    <Card key={client.id} className="border border-gray-200 hover:shadow-md transition-all">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <Avatar className="w-12 h-12">
                              <AvatarImage src={clientAvatarSrc} alt={fullName} />
                              <AvatarFallback className="bg-primary text-black text-sm font-semibold">
                                {initials}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <h3 className="font-semibold text-lg text-gray-800 cursor-pointer hover:underline" onClick={() => setViewingClientId(client.id)}>{fullName}</h3>
                              <p className="text-gray-600 text-sm">{client.email}</p>
                              {client.goals && client.goals.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {client.goals.slice(0, 3).map((goal, i) => (
                                    <Badge key={i} variant="secondary" className="text-xs">{goal}</Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="min-w-[220px] text-right">
                            <p className="text-xs text-gray-500">Last Workout</p>
                            <p className="font-medium text-gray-800">{formatDate(client.lastWorkoutDate)}</p>
                            {client.lastWorkoutType && (
                              <p className="text-xs text-gray-500">{client.lastWorkoutType}</p>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {client.trainer ? (
                              <>
                                <Avatar className="w-8 h-8">
                                  <AvatarImage
                                    src={resolveBackendAssetUrl(client.trainer.avatarUrl) || undefined}
                                    alt={client.trainer.name}
                                  />
                                  <AvatarFallback className="bg-gradient-to-br from-blue-400 to-blue-500 text-white text-xs">
                                    {client.trainer.initials}
                                  </AvatarFallback>
                                </Avatar>
                                <button
                                  className="text-sm font-medium text-gray-800 hover:underline"
                                  onClick={() => openAssign(client.id)}
                                >
                                  {client.trainer.name}
                                </button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={async () => {
                                    const clientId = client.id;
                                    const previousTrainer = client.trainer;
                                    queryClient.setQueryData<AdminClient[]>(clientsQueryKey, (prev = []) =>
                                      prev.map((c) => (c.id === clientId ? { ...c, trainer: null } : c))
                                    );
                                    toast({
                                      title: "Trainer unassigned",
                                      description: "Unassignment is being saved…",
                                    });
                                    assignTrainer(clientId, null)
                                      .then(async () => {
                                        await queryClient.invalidateQueries({ queryKey: clientsQueryKey });
                                      })
                                      .catch((error: any) => {
                                        console.error("Error removing trainer:", error);
                                        queryClient.setQueryData<AdminClient[]>(clientsQueryKey, (prev = []) =>
                                          prev.map((c) =>
                                            c.id === clientId ? { ...c, trainer: previousTrainer } : c
                                          )
                                        );
                                        toast({
                                          title: "Error removing trainer",
                                          description: error?.message ?? String(error),
                                          variant: "destructive",
                                        });
                                      });
                                  }}
                                >
                                  Remove Trainer
                                </Button>
                              </>
                            ) : (
                              <Button variant="outline" size="sm" onClick={() => openAssign(client.id)}>
                                Assign Trainer
                              </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => setViewingClientId(client.id)}>View Profile</Button>
                          </div>

                          <div className="flex gap-2">
                            <Badge variant="outline" className="text-xs">{client.workoutsThisMonth} this month</Badge>
                            <Badge variant="outline" className="text-xs">{client.totalWorkouts} total</Badge>
                            <Badge variant="outline" className="text-xs">{client.pointsEarned} pts</Badge>
                          </div>
                        </div>

                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex flex-wrap items-center gap-2">
                            {client.pricing_tier ? (
                              <Badge variant="outline" className="text-xs">Plan: {client.pricing_tier}</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">Plan: None</Badge>
                            )}
                            <span className="text-xs text-gray-500">Last payment: {formatDate(client.last_payment_date)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {filteredClients.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">No clients found.</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-white text-black">
          <DialogHeader>
            <DialogTitle>Assign Trainer</DialogTitle>
            <DialogDescription>Select a trainer or admin to assign to this client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Trainer</Label>
            <Select value={selectedTrainerId} onValueChange={setSelectedTrainerId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a trainer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_TRAINER_VALUE}>None (Unassign)</SelectItem>
                {trainerOptions.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-gray-300 text-gray-800 hover:bg-gray-100 hover:text-gray-900" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveAssignment}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type EquipmentItem = { id: string; name: string; category: string; brand: string; status: string };
type ExerciseItem = { id: string; name: string; muscleGroup: string; equipment: string };
type WorkoutItem = { id: string; title: string; difficulty: string; exercisesCount: string };
type ProgramItem = { id: string; name: string; durationWeeks: string; workoutsCount: string };
type UserItem = { id: string; name: string; email: string; role: string };

const AdminPortal = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile, isAdmin, isTrainer, signOut } = useAuth();
  
  // Initialize activeTab from URL, fallback to localStorage for migration
  const getInitialTab = (): "equipment" | "exercises" | "workouts" | "programs" | "lessons" | "notifications" | "clients" | "prompts" | "users" | "contacts" | "legal" | "pricing" | "faqs" | "settings" | "profile" => {
    const urlTab = searchParams.get("tab");
    if (urlTab) {
      const movedToSettings = ["notifications", "legal", "pricing", "faqs", "prompts"];
      const tab = movedToSettings.includes(urlTab) ? "settings" : urlTab;
      const validTabs = ["equipment", "exercises", "workouts", "programs", "lessons", "notifications", "clients", "prompts", "users", "contacts", "legal", "pricing", "faqs", "settings", "profile"];
      if (validTabs.includes(tab)) {
        return tab as any;
      }
    }
    // Fallback to localStorage for migration
    const stored = (localStorage.getItem("admin-active-tab") || "clients") as string;
    const movedToSettings = ["notifications", "legal", "pricing", "faqs", "prompts"];
    return (movedToSettings.includes(stored) ? "settings" : stored) as any;
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  const [workoutAssignmentContext, setWorkoutAssignmentContext] = useState<{
    clientId: string;
    assignmentDate: Date | null;
    backToClientId: string;
    assignedWorkoutId?: string;
  } | null>(null);

  // Initialize nested client view state from URL
  const [viewingClientId, setViewingClientId] = useState<string | null>(() => searchParams.get("clientId") || null);
  const [clientProfileTab, setClientProfileTab] = useState<string>(() => searchParams.get("clientTab") || "overview");
  const [clientWorkoutView, setClientWorkoutView] = useState<"history" | "scheduled">(() => {
    const view = searchParams.get("workoutView");
    return (view === "scheduled" || view === "history") ? view : "history";
  });

  // Sync activeTab with URL
  useEffect(() => {
    const currentTab = searchParams.get("tab");
    if (currentTab !== activeTab) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("tab", activeTab);
      // If switching away from clients tab, clear client-related params
      if (activeTab !== "clients") {
        newParams.delete("clientId");
        newParams.delete("clientTab");
        newParams.delete("workoutView");
        newParams.delete("phaseId");
      }
      // If switching away from settings tab, clear settingsTab param
      if (activeTab !== "settings") {
        newParams.delete("settingsTab");
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  // Sync nested client view state FROM URL (when ClientProfileView updates it)
  // This reads from URL and updates AdminPortal's state when URL changes
  useEffect(() => {
    if (activeTab === "clients") {
      const urlClientTab = searchParams.get("clientTab");
      const urlWorkoutView = searchParams.get("workoutView");
      
      // Always sync state from URL when on clients tab (ClientProfileView is source of truth)
      // Use functional updates to avoid needing state values in dependencies
      if (urlClientTab) {
        setClientProfileTab(prev => prev !== urlClientTab ? urlClientTab : prev);
      }
      if (urlWorkoutView === "scheduled" || urlWorkoutView === "history") {
        setClientWorkoutView(prev => prev !== urlWorkoutView ? urlWorkoutView : prev);
      }
    }
  }, [searchParams, activeTab]); // Only depend on searchParams and activeTab to read from URL

  // Sync nested client view state TO URL when on clients tab (only for initial setup when viewingClientId is set)
  useEffect(() => {
    if (activeTab === "clients" && viewingClientId) {
      const urlClientId = searchParams.get("clientId");
      const urlClientTab = searchParams.get("clientTab");
      
      // Only update URL if clientId is missing or different (initial setup or client changed)
      // Don't update if URL already has clientTab (let ClientProfileView manage it)
      if (urlClientId !== viewingClientId) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("tab", "clients");
        newParams.set("clientId", viewingClientId);
        // Only set defaults if URL doesn't already have them
        if (!urlClientTab) {
          newParams.set("clientTab", clientProfileTab);
        }
        if (!searchParams.get("workoutView")) {
          newParams.set("workoutView", clientWorkoutView);
        }
        setSearchParams(newParams, { replace: true });
      }
    } else if (activeTab === "clients" && !viewingClientId) {
      // Clear URL params when no client is being viewed
      const urlClientId = searchParams.get("clientId");
      if (urlClientId) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("tab", "clients");
        newParams.delete("clientId");
        newParams.delete("clientTab");
        newParams.delete("workoutView");
        newParams.delete("phaseId");
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [activeTab, viewingClientId, searchParams, setSearchParams]); // Don't depend on clientProfileTab/clientWorkoutView to avoid loops

  // Clear workoutAssignmentContext when switching to workouts tab for template editing
  // This ensures templates are edited without assignment context interfering
  // Only clear if we're switching TO workouts (not staying in clients)
  useEffect(() => {
    if (activeTab === "workouts" && workoutAssignmentContext) {
      // Clear the assignment context when switching to workouts tab
      // This means we're editing templates, not assigned workouts
      setWorkoutAssignmentContext(null);
    }
  }, [activeTab]);

  // Restore client view when coming back from assigned workout editing
  // This handles cleanup when leaving the clients tab after editing an assigned workout
  useEffect(() => {
    if (activeTab !== "clients" && activeTab !== "workouts" && workoutAssignmentContext) {
      // We've navigated away from both clients and workouts tabs
      // Clean up the assignment context
      // Note: URL params are already cleared by the activeTab sync effect
      setWorkoutAssignmentContext(null);
    }
  }, [activeTab, workoutAssignmentContext]);

  // Ensure a body class is present while admin portal is mounted so
  // portal-based dialogs can be themed within the admin scope.
  useEffect(() => {
    document.body.classList.add("admin-portal-active");
    return () => {
      document.body.classList.remove("admin-portal-active");
    };
  }, []);

  const sidebarItems = [
    { id: "settings", title: "Settings", icon: Settings },
    { id: "contacts", title: "Contact Submissions", icon: MessageSquare },
    { id: "users", title: "Users", icon: Users },
    { id: "clients", title: "Clients", icon: Users },
    { id: "profile", title: "Profile Settings", icon: User },
    { id: "programs", title: "Programs", icon: FileText },
    { id: "workouts", title: "Workouts", icon: Dumbbell },
    { id: "exercises", title: "Exercises", icon: Activity },
    { id: "equipment", title: "Equipment", icon: Wrench },
    { id: "lessons", title: "Lessons", icon: FileText },
  ] as const;

  const equipmentInitial: EquipmentItem[] = [
    { id: "1", name: "Dumbbell 10kg", category: "Weights", brand: "Rogue", status: "Available" },
    { id: "2", name: "Kettlebell 16kg", category: "Weights", brand: "Eleiko", status: "In Use" },
  ];
  const exercisesInitial: ExerciseItem[] = [
    { id: "1", name: "Squat", muscleGroup: "Legs", equipment: "Barbell" },
    { id: "2", name: "Bench Press", muscleGroup: "Chest", equipment: "Barbell" },
  ];
  const workoutsInitial: WorkoutItem[] = [
    { id: "1", title: "Full Body Beginner", difficulty: "Easy", exercisesCount: "8" },
    { id: "2", title: "Upper Body Strength", difficulty: "Medium", exercisesCount: "10" },
  ];
  const programsInitial: ProgramItem[] = [
    { id: "1", name: "8-Week Strength", durationWeeks: "8", workoutsCount: "24" },
    { id: "2", name: "4-Week Fat Loss", durationWeeks: "4", workoutsCount: "16" },
  ];
  const usersInitial: UserItem[] = [
    { id: "1", name: "Alex Johnson", email: "alex@example.com", role: "client" },
    { id: "2", name: "Sam Lee", email: "sam@example.com", role: "trainer" },
  ];

  // Workouts filters state
  const [workoutSearch, setWorkoutSearch] = useState("");
  const [workoutCategory, setWorkoutCategory] = useState("all");

  // Equipment items and create dialog state
  type EquipmentCard = {
    id: string;
    name: string;
    image?: string | null;
    description: string;
    instructions?: string;
    affiliateUrl?: string | null;
    category: string;
    isDeleted: boolean;
    isStandard: boolean;
    aiGenerated: boolean;
  };
  const [equipmentCatalogSourceFilter, setEquipmentCatalogSourceFilter] = useState<CatalogSourceFilter>("all");
  const [equipDialogOpen, setEquipDialogOpen] = useState(false);
  const [newEquipName, setNewEquipName] = useState("");
  const [newEquipDescription, setNewEquipDescription] = useState("");
  const [newEquipImagePreview, setNewEquipImagePreview] = useState<string | null>(null);
  const [newEquipImageFile, setNewEquipImageFile] = useState<File | null>(null);
  const [newEquipInstructions, setNewEquipInstructions] = useState("");
  const [newEquipAffiliateUrl, setNewEquipAffiliateUrl] = useState("");
  const [editingEquipId, setEditingEquipId] = useState<string | null>(null);
  const [showDeletedEquipment, setShowDeletedEquipment] = useState(false);
  const [equipToDeleteId, setEquipToDeleteId] = useState<string | null>(null);
  const [newEquipIsStandard, setNewEquipIsStandard] = useState(false);
  const [equipmentError, setEquipmentError] = useState<string | null>(null);

  function mapDbEquipmentToCard(row: Tables<"equipment"> & { file_path?: string | null }): EquipmentCard {
    const rawImageUrl = (row as any).image_file || (row as any).image_url || row.file_path;
    const resolvedImageUrl = resolveBackendAssetUrl(rawImageUrl);
    return {
      id: row.id,
      name: row.name,
      image: resolvedImageUrl || null,
      description: row.description || "",
      instructions: row.instructions || "",
      affiliateUrl: (row as any).affiliate_url || null,
      category: (row as any).category || "Other",
      isDeleted: Boolean(row.is_deleted),
      isStandard: Boolean((row as any).is_standard),
      aiGenerated: Boolean((row as any).ai_generated),
    };
  }

  function generateUniquePath(fileName: string) {
    const ext = (fileName.split(".").pop() || "jpg").toLowerCase();
    const unique = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    return `equipment/${unique}.${ext}`;
  }

  const equipmentQueryKey = queryKeys.equipment.all;
  const equipmentQuery = useQuery({
    queryKey: equipmentQueryKey,
    queryFn: async () => {
      const data = await listEquipment();
      return (data as any[]).map((r) => mapDbEquipmentToCard(r as any));
    },
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...equipmentQueryKey]),
  });
  const equipmentItems = equipmentQuery.data ?? [];
  const loadingEquipment = equipmentQuery.isPending;

  useEffect(() => {
    if (equipmentQuery.isError && equipmentQuery.error) {
      const msg = String((equipmentQuery.error as Error)?.message || equipmentQuery.error);
      setEquipmentError(msg);
      toast({ title: "Failed to load equipment", description: msg, variant: "destructive" as any });
    }
  }, [equipmentQuery.isError, equipmentQuery.error]);

  // Users state for enhanced UI
  type AdminUser = {
    id: string;
    name: string;
    email: string;
    role: string;
    avatarUrl?: string;
    status: "active" | "inactive";
    lastLogin: string; // ISO date
    createdAt: string; // ISO date
  };
  const [userSearch, setUserSearch] = useState("");
  const [userStatusFilter, setUserStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([
    {
      id: "u1",
      name: "Alex Johnson",
      email: "alex@example.com",
      role: "client",
      status: "active",
      lastLogin: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: "u2",
      name: "Sarah Chen",
      email: "sarah@example.com",
      role: "trainer",
      status: "active",
      lastLogin: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - 300 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: "u3",
      name: "Admin User",
      email: "admin@example.com",
      role: "admin",
      status: "inactive",
      lastLogin: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - 500 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ]);
  const filteredAdminUsers = adminUsers
    .filter(u => u.name.toLowerCase().includes(userSearch.toLowerCase()) || u.email.toLowerCase().includes(userSearch.toLowerCase()))
    .filter(u => (userStatusFilter === "all" ? true : u.status === userStatusFilter));
  const toggleUserStatus = (id: string) => {
    setAdminUsers(prev => prev.map(u => u.id === id ? { ...u, status: u.status === "active" ? "inactive" : "active" } : u));
  };
  const formatDateShort = (iso: string) => new Date(iso).toLocaleDateString();
  const [confirmUserId, setConfirmUserId] = useState<string | null>(null);

  // Add User dialog state
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUserFirst, setNewUserFirst] = useState("");
  const [newUserLast, setNewUserLast] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState<"admin" | "trainer" | "">("");
  const saveNewUser = () => {
    if (!newUserFirst.trim() || !newUserLast.trim() || !newUserEmail.trim() || !newUserRole) return;
    const fullName = `${newUserFirst.trim()} ${newUserLast.trim()}`;
    const now = new Date().toISOString();
    const newUser: AdminUser = {
      id: `u${Date.now()}`,
      name: fullName,
      email: newUserEmail.trim(),
      role: newUserRole,
      status: "active",
      lastLogin: now,
      createdAt: now,
    };
    setAdminUsers(prev => [newUser, ...prev]);
    setAddUserOpen(false);
    setNewUserFirst("");
    setNewUserLast("");
    setNewUserEmail("");
    setNewUserRole("");
  };

  // Prompts state and edit dialog
  type AdminPrompt = { id: string; name: string; slug: string; description: string };
  const [prompts, setPrompts] = useState<AdminPrompt[]>([
    { id: "p1", name: "Welcome Message", slug: "welcome-message", description: "Initial greeting sent to new clients" },
    { id: "p2", name: "Workout Reminder", slug: "workout-reminder", description: "Reminder sent on scheduled workout days" },
    { id: "p3", name: "Progress Check-in", slug: "progress-check-in", description: "Monthly progress follow-up prompt" },
  ]);
  const [editPromptOpen, setEditPromptOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<AdminPrompt | null>(null);
  const [promptName, setPromptName] = useState("");
  const [promptSlug, setPromptSlug] = useState("");
  const [promptDescription, setPromptDescription] = useState("");
  const openEditPrompt = (p: AdminPrompt) => {
    setEditingPrompt(p);
    setPromptName(p.name);
    setPromptSlug(p.slug);
    setPromptDescription(p.description);
    setEditPromptOpen(true);
  };
  const savePrompt = () => {
    if (!editingPrompt) return;
    setPrompts(prev => prev.map(pr => pr.id === editingPrompt.id ? { ...pr, name: promptName.trim(), slug: promptSlug.trim(), description: promptDescription.trim() } : pr));
    setEditPromptOpen(false);
    setEditingPrompt(null);
  };

  // Lessons state and dialog
  type AdminLesson = {
    id: string;
    title: string;
    type: "Rich Text" | "Video";
    category: string;
    image: string;
    shortDescription: string;
    content?: string;
    videoUrl?: string;
  };
  const [lessons, setLessons] = useState<AdminLesson[]>([
    { id: "l1", title: "Basics of Muscle Building", type: "Rich Text", category: "Best Practices", image: "https://images.unsplash.com/photo-1517963879433-6ad2b056d712?w=1200&auto=format&fit=crop&q=60", shortDescription: "Foundations for hypertrophy" },
    { id: "l2", title: "How to Perfect Your Squat", type: "Video", category: "Exercise Technique", image: "https://images.unsplash.com/photo-1579758629938-03607ccdbaba?w=1200&auto=format&fit=crop&q=60", shortDescription: "Step-by-step squat tutorial", videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ" },
    { id: "l3", title: "Nutrition for Fitness Beginners", type: "Rich Text", category: "Nutrition", image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1200&auto=format&fit=crop&q=60", shortDescription: "Getting started with nutrition" },
  ]);
  const [lessonDialogOpen, setLessonDialogOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState<AdminLesson | null>(null);
  const [lessonTitle, setLessonTitle] = useState("");
  const [lessonShortDesc, setLessonShortDesc] = useState("");
  const [lessonType, setLessonType] = useState<"Rich Text" | "Video" | "">("");
  const [lessonContent, setLessonContent] = useState("");
  const [lessonVideoUrl, setLessonVideoUrl] = useState("");
  const [lessonImage, setLessonImage] = useState<string | null>(null);
  const openLessonDialogFor = (l?: AdminLesson) => {
    if (l) {
      setEditingLesson(l);
      setLessonTitle(l.title);
      setLessonShortDesc(l.shortDescription);
      setLessonType(l.type);
      setLessonContent(l.content || "");
      setLessonVideoUrl(l.videoUrl || "");
      setLessonImage(l.image || null);
    } else {
      setEditingLesson(null);
      setLessonTitle("");
      setLessonShortDesc("");
      setLessonType("");
      setLessonContent("");
      setLessonVideoUrl("");
      setLessonImage(null);
    }
    setLessonDialogOpen(true);
  };
  const saveLesson = () => {
    if (!lessonTitle.trim() || !lessonType) return;
    if (editingLesson) {
      setLessons(prev => prev.map(x => x.id === editingLesson.id ? {
        ...x,
        title: lessonTitle.trim(),
        shortDescription: lessonShortDesc.trim(),
        type: lessonType as any,
        content: lessonContent.trim() || undefined,
        videoUrl: lessonVideoUrl.trim() || undefined,
        image: lessonImage || x.image,
      } : x));
    } else {
      setLessons(prev => [{
        id: `l${Date.now()}`,
        title: lessonTitle.trim(),
        shortDescription: lessonShortDesc.trim(),
        type: lessonType as any,
        category: "General",
        content: lessonContent.trim() || undefined,
        videoUrl: lessonVideoUrl.trim() || undefined,
        image: lessonImage || "https://images.unsplash.com/photo-1517963879433-6ad2b056d712?w=1200&auto=format&fit=crop&q=60",
      }, ...prev]);
    }
    setLessonDialogOpen(false);
  };

  const programsQueryKey = queryKeys.programs.all;
  const programsQuery = useQuery({
    queryKey: programsQueryKey,
    queryFn: listPrograms,
    enabled: activeTab === "programs",
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...programsQueryKey]),
  });
  const programs = programsQuery.data ?? [];
  const programsLoading = programsQuery.isPending;

  // Programs dialog
  const [programDialogOpen, setProgramDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);
  const [deleteProgramId, setDeleteProgramId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programName, setProgramName] = useState("");
  const [programDescription, setProgramDescription] = useState("");
  const [programDuration, setProgramDuration] = useState<number>(12);
  const [programFrequency, setProgramFrequency] = useState<number>(3);
  const [programWeeklySchedulePreference, setProgramWeeklySchedulePreference] = useState("");
  const [programDifficulty, setProgramDifficulty] = useState<DifficultyEnum>("Beginner");
  const [programMotivations, setProgramMotivations] = useState<string[]>([]);
  const [programExerciseTypes, setProgramExerciseTypes] = useState<string[]>([]);
  const [programExerciseTypeDetails, setProgramExerciseTypeDetails] = useState<Record<string, string>>({});
  const [programFocus, setProgramFocus] = useState("");
  const [programWhatToExpect, setProgramWhatToExpect] = useState("");
  const [programVariety, setProgramVariety] = useState("");
  
  const openProgramDialogFor = (p?: Program) => {
    if (p) {
      setEditingProgram(p);
      setProgramName(p.name);
      setProgramDescription(p.description);
      setProgramDuration(p.duration);
      setProgramFrequency(p.frequency);
      setProgramWeeklySchedulePreference(p.weekly_schedule_preference ?? "");
      setProgramDifficulty(p.difficulty as DifficultyEnum);
      setProgramMotivations([...p.motivations]);
      setProgramExerciseTypes([...p.exercise_types]);
      setProgramExerciseTypeDetails({ ...(p.exercise_type_details || {}) });
      setProgramFocus(p.program_focus);
      setProgramWhatToExpect(p.what_to_expect);
      setProgramVariety(p.variety ?? "");
    } else {
      setEditingProgram(null);
      setProgramName("");
      setProgramDescription("");
      setProgramDuration(12);
      setProgramFrequency(3);
      setProgramWeeklySchedulePreference("");
      setProgramDifficulty("Beginner");
      setProgramMotivations([]);
      setProgramExerciseTypes([]);
      setProgramExerciseTypeDetails({});
      setProgramFocus("");
      setProgramWhatToExpect("");
      setProgramVariety("");
    }
    setProgramDialogOpen(true);
  };
  
  const toggleMotivation = (motivationId: string) => {
    setProgramMotivations(prev => 
      prev.includes(motivationId)
        ? prev.filter(id => id !== motivationId)
        : [...prev, motivationId]
    );
  };
  
  const toggleExerciseType = (exerciseTypeId: string) => {
    setProgramExerciseTypes(prev => {
      const next = prev.includes(exerciseTypeId)
        ? prev.filter(id => id !== exerciseTypeId)
        : [...prev, exerciseTypeId];
      // Drop details for unselected types
      setProgramExerciseTypeDetails((d) => {
        const out: Record<string, string> = {};
        for (const id of next) {
          if (typeof d[id] === "string" && d[id].trim()) out[id] = d[id];
        }
        return out;
      });
      return next;
    });
  };
  
  const saveProgram = async () => {
    if (!programName.trim() || !programDescription.trim() || programDuration <= 0 || programFrequency <= 0) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }
    try {
      if (editingProgram) {
        await updateProgram(editingProgram.id, {
          name: programName.trim(),
          description: programDescription.trim(),
          duration: programDuration,
          frequency: programFrequency,
          weekly_schedule_preference: programWeeklySchedulePreference.trim(),
          difficulty: programDifficulty,
          motivations: programMotivations,
          exercise_types: programExerciseTypes,
          exercise_type_details: programExerciseTypeDetails,
          program_focus: programFocus.trim(),
          what_to_expect: programWhatToExpect.trim(),
          variety: programVariety.trim(),
        });
        toast({
          title: "Success",
          description: "Program updated successfully.",
        });
      } else {
        await createProgram({
          name: programName.trim(),
          description: programDescription.trim(),
          duration: programDuration,
          frequency: programFrequency,
          weekly_schedule_preference: programWeeklySchedulePreference.trim(),
          difficulty: programDifficulty,
          motivations: programMotivations,
          exercise_types: programExerciseTypes,
          exercise_type_details: programExerciseTypeDetails,
          program_focus: programFocus.trim(),
          what_to_expect: programWhatToExpect.trim(),
          variety: programVariety.trim(),
        });
        toast({
          title: "Success",
          description: "Program created successfully.",
        });
      }
      setProgramDialogOpen(false);
      await queryClient.invalidateQueries({ queryKey: programsQueryKey });
    } catch (error: any) {
      console.error("Error saving program:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save program. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDuplicateProgram = async (program: Program) => {
    try {
      await duplicateProgram(program.id, `${program.name} copy`);
      toast({
        title: "Success",
        description: "Program duplicated successfully.",
      });
      await queryClient.invalidateQueries({ queryKey: programsQueryKey });
    } catch (error: any) {
      console.error("Error duplicating program:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to duplicate program. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteProgram = async () => {
    if (!deleteProgramId) return;
    try {
      await deleteProgram(deleteProgramId);
      toast({
        title: "Success",
        description: "Program deleted successfully.",
      });
      setDeleteDialogOpen(false);
      setDeleteProgramId(null);
      await queryClient.invalidateQueries({ queryKey: programsQueryKey });
    } catch (error: any) {
      console.error("Error deleting program:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete program. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getDifficultyColor = (level: string) => {
    const colorMap: Record<string, string> = {
      Beginner: "bg-green-100 text-green-800",
      Intermediate: "bg-yellow-100 text-yellow-800",
      Advanced: "bg-blue-100 text-blue-800",
      Expert: "bg-orange-100 text-orange-800",
      Pro: "bg-red-100 text-red-800",
    };
    return colorMap[level] || "bg-gray-100 text-gray-800";
  };

  const renderTab = () => {
    switch (activeTab) {
      case "equipment":
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Equipment</h1>
                <p className="text-gray-600">Same view as the trainer portal for consistency.</p>
              </div>
              {isAdmin && (
                <Button onClick={() => {
                  setEditingEquipId(null);
                  setNewEquipName("");
                  setNewEquipDescription("");
                  setNewEquipInstructions("");
                  setNewEquipAffiliateUrl("");
                  setNewEquipImagePreview(null);
                  setNewEquipImageFile(null);
                  setNewEquipIsStandard(false);
                  setEquipmentError(null);
                  setEquipDialogOpen(true);
                }}>Add Equipment</Button>
              )}
            </div>

            <div className="flex items-center gap-4 border-b border-gray-200 pb-2">
              <button
                onClick={() => setShowDeletedEquipment(false)}
                className={`px-4 py-2 font-medium transition-colors ${
                  !showDeletedEquipment
                    ? "text-primary border-b-2 border-primary"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Active Equipment
              </button>
              {isAdmin && (
              <button
                onClick={() => setShowDeletedEquipment(true)}
                className={`px-4 py-2 font-medium transition-colors ${
                  showDeletedEquipment
                    ? "text-primary border-b-2 border-primary"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Deleted Equipment
              </button>
              )}
            </div>

            <CatalogSourceFilterSelect
              value={equipmentCatalogSourceFilter}
              onChange={setEquipmentCatalogSourceFilter}
            />

                  {(() => {
              const statusFilteredEquipment = equipmentItems.filter((eq) =>
                showDeletedEquipment ? eq.isDeleted : !eq.isDeleted
              );
              const visibleEquipment = statusFilteredEquipment.filter((eq) =>
                matchesCatalogSourceFilter(eq.aiGenerated, equipmentCatalogSourceFilter)
              );
              const filteredOutBySource =
                statusFilteredEquipment.length > 0 && visibleEquipment.length === 0;
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {loadingEquipment && (
                          <div className="col-span-full text-center text-gray-600">Loading equipment…</div>
                        )}
                        {equipmentError && (
                          <div className="col-span-full text-center text-red-600">{equipmentError}</div>
                        )}
                        {!loadingEquipment && !equipmentError && visibleEquipment.length === 0 && (
                          <div className="col-span-full">
                            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center">
                              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
                                <Trash2 className="h-6 w-6 text-gray-500" />
                              </div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {filteredOutBySource
                                  ? "No equipment matches this filter"
                                  : showDeletedEquipment
                                    ? "No deleted equipment"
                                    : "No active equipment"}
                              </h3>
                              <p className="mt-1 text-sm text-gray-600">
                                {filteredOutBySource
                                  ? "Try choosing a different option under Added by."
                                  : showDeletedEquipment
                                    ? "Deleted equipment will show up here. You can reactivate items from this list."
                                    : "Add equipment to get started."}
                              </p>
                            </div>
                          </div>
                        )}
                  {visibleEquipment.map((eq) => (
                <Card key={eq.id} className="group relative rounded-xl overflow-hidden hover:shadow-xl transition-all bg-white border border-gray-200/60">
                  <div className="relative w-full aspect-[4/3] bg-gray-100">
                    {eq.image ? (
                      <img
                        src={eq.image}
                        alt={eq.name}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          // No dummy fallback images: if it fails, hide the image and show the neutral background.
                          e.currentTarget.onerror = null;
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : null}
                    {eq.image ? (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                    ) : null}
                    
                    {isAdmin && (
                      <div className="absolute top-3 right-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="bg-white/90 hover:bg-white rounded-full h-8 w-8 shadow-sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="w-4 h-4 text-gray-700" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white text-black z-50">
                            {!eq.isDeleted && (
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingEquipId(eq.id);
                                  setNewEquipName(eq.name);
                                  setNewEquipDescription(eq.description);
                                  setNewEquipInstructions(eq.instructions || "");
                                  setNewEquipAffiliateUrl(eq.affiliateUrl || "");
                                  setNewEquipImagePreview(eq.image || null);
                                  setNewEquipIsStandard(eq.isStandard);
                                  setEquipmentError(null);
                                  setEquipDialogOpen(true);
                                }}>
                                <Edit3 className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                            )}
                            {!eq.isDeleted ? (
                              <DropdownMenuItem 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEquipToDeleteId(eq.id);
                                }}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await reactivateEquipment(eq.id);
                                    await queryClient.invalidateQueries({ queryKey: equipmentQueryKey });
                                    await queryClient.invalidateQueries({ queryKey: queryKeys.equipment.options });
                                    toast({ title: "Equipment reactivated" });
                                  } catch (error: any) {
                                    toast({ title: "Failed to reactivate", description: error.message, variant: "destructive" as any });
                                  }
                                }}
                                className="text-green-600 focus:text-green-600"
                              >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Reactivate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                    {eq.isStandard && (
                      <div className="absolute top-3 left-3">
                        <Badge variant="outline" className="bg-white/90 text-emerald-700 border-emerald-200 text-xs">Standard</Badge>
                      </div>
                    )}
                    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                      <CardTitle className={eq.image ? "text-white text-lg drop-shadow" : "text-gray-900 text-lg"}>
                        {eq.name}
                      </CardTitle>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-700 line-clamp-3">{eq.description}</p>
                  </CardContent>
                </Card>
                  ))}
                </div>
              );
            })()}

            <Dialog open={equipDialogOpen} onOpenChange={(open) => { 
              setEquipDialogOpen(open); 
              if (!open) { 
                setEditingEquipId(null);
                setEquipmentError(null);
                setNewEquipName("");
                setNewEquipDescription("");
                setNewEquipInstructions("");
                setNewEquipAffiliateUrl("");
                setNewEquipImagePreview(null);
                setNewEquipImageFile(null);
                setNewEquipIsStandard(false);
              } 
            }}>
              <DialogScrollContent className="bg-white text-black">
                <DialogHeader className="shrink-0">
                  <DialogTitle>{editingEquipId ? "Edit Equipment" : "Add Equipment"}</DialogTitle>
                  <DialogDescription>Upload an image and provide details {editingEquipId ? "to update the equipment." : "for the new equipment."}</DialogDescription>
                </DialogHeader>
                <DialogBody className="space-y-4 py-1 pr-1">
                  <div className="space-y-2">
                    <Label htmlFor="equip-name">Name</Label>
                    <Input id="equip-name" value={newEquipName} onChange={(e) => {
                      setNewEquipName(e.target.value);
                      setEquipmentError(null);
                    }} placeholder="e.g., Adjustable Dumbbells" />
                    {equipmentError && equipmentError.includes("already exists") && (
                      <p className="text-sm text-red-600">{equipmentError}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="equip-description">Description</Label>
                    <Textarea id="equip-description" value={newEquipDescription} onChange={(e) => setNewEquipDescription(e.target.value)} placeholder="Short description..." />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="equip-instructions">Instructions</Label>
                    <Textarea id="equip-instructions" value={newEquipInstructions} onChange={(e) => setNewEquipInstructions(e.target.value)} placeholder="Setup and usage tips..." />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="equip-affiliate-url">Affiliate URL</Label>
                    <Input
                      id="equip-affiliate-url"
                      type="url"
                      value={newEquipAffiliateUrl}
                      onChange={(e) => setNewEquipAffiliateUrl(e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Image</Label>
                    <Input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) {
                        if (newEquipImagePreview && newEquipImagePreview.startsWith("blob:")) {
                          URL.revokeObjectURL(newEquipImagePreview);
                        }
                        setNewEquipImagePreview(null);
                        setNewEquipImageFile(null);
                        return;
                      }
                      const lowerName = file.name.toLowerCase();
                      const isHeic = file.type === "image/heic" || file.type === "image/heif" || lowerName.endsWith(".heic") || lowerName.endsWith(".heif");
                      if (isHeic) {
                        toast({
                          title: "Unsupported image format",
                          description: "HEIC/HEIF images aren’t supported in the browser. Please upload a JPG, PNG, or WebP.",
                          variant: "destructive" as any,
                        });
                        e.target.value = "";
                        if (newEquipImagePreview && newEquipImagePreview.startsWith("blob:")) {
                          URL.revokeObjectURL(newEquipImagePreview);
                        }
                        setNewEquipImagePreview(null);
                        setNewEquipImageFile(null);
                        return;
                      }
                      if (newEquipImagePreview && newEquipImagePreview.startsWith("blob:")) {
                        URL.revokeObjectURL(newEquipImagePreview);
                      }
                      const url = URL.createObjectURL(file);
                      setNewEquipImagePreview(url);
                      setNewEquipImageFile(file);
                    }} />
                    {newEquipImagePreview && (
                      <div className="mt-2">
                        <img src={newEquipImagePreview} alt="Preview" className="h-32 w-full object-cover rounded-md border" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 pt-1">
                    <Checkbox
                      id="equip-standard"
                      checked={newEquipIsStandard}
                      onCheckedChange={(v) => setNewEquipIsStandard(Boolean(v))}
                      className="h-5 w-5 border-black data-[state=checked]:bg-black data-[state=checked]:border-black"
                    />
                    <Label htmlFor="equip-standard" className="cursor-pointer">Standard equipment</Label>
                  </div>
                </DialogBody>
                <DialogFooter className="shrink-0 pt-2 border-t border-gray-200">
                  <Button variant="outline" className="border-gray-300 text-gray-800 hover:bg-gray-100 hover:text-gray-900" onClick={() => {
                    setEquipDialogOpen(false);
                  }}>Cancel</Button>
                  <Button onClick={async () => {
                    if (!newEquipName.trim()) { return; }
                    
                    const trimmedName = newEquipName.trim();
                    const trimmedAffiliateUrl = newEquipAffiliateUrl.trim();
                    if (trimmedAffiliateUrl && !/^https?:\/\//i.test(trimmedAffiliateUrl)) {
                      toast({
                        title: "Invalid affiliate URL",
                        description: "Affiliate URL must start with http:// or https://",
                        variant: "destructive" as any,
                      });
                      return;
                    }
                    
                    // Check for duplicate name before creating
                    if (!editingEquipId) {
                      const existingNames = equipmentItems.map(item => item.name.toLowerCase());
                      if (existingNames.includes(trimmedName.toLowerCase())) {
                        setEquipmentError(`Equipment with name "${trimmedName}" already exists`);
                        toast({ title: "Duplicate name", description: `An equipment item with the name "${trimmedName}" already exists. Please choose a different name.`, variant: "destructive" as any });
                        return;
                      }
                    } else {
                      // When editing, check if name conflicts with other equipment
                      const conflictingItem = equipmentItems.find(item => 
                        item.name.toLowerCase() === trimmedName.toLowerCase() && item.id !== editingEquipId
                      );
                      if (conflictingItem) {
                        setEquipmentError(`Equipment with name "${trimmedName}" already exists`);
                        toast({ title: "Duplicate name", description: `Another equipment item already has the name "${trimmedName}". Please choose a different name.`, variant: "destructive" as any });
                        return;
                      }
                    }
                    
                    setEquipmentError(null);
                    try {
                      if (editingEquipId) {
                        await updateEquipment(
                          editingEquipId,
                          {
                            name: trimmedName,
                            description: newEquipDescription.trim() || null,
                            // Don't send category - let backend keep existing value
                            instructions: newEquipInstructions.trim() || null,
                            affiliate_url: trimmedAffiliateUrl || null,
                            is_standard: newEquipIsStandard,
                          },
                          newEquipImageFile || undefined
                        );
                        await queryClient.invalidateQueries({ queryKey: equipmentQueryKey });
                        await queryClient.invalidateQueries({ queryKey: queryKeys.equipment.options });
                        toast({ title: "Equipment updated" });
                      } else {
                        await createEquipment(
                          {
                            name: trimmedName,
                            description: newEquipDescription.trim() || null,
                            category: null, // category not currently editable in UI
                            instructions: newEquipInstructions.trim() || null,
                            affiliate_url: trimmedAffiliateUrl || null,
                            is_standard: newEquipIsStandard,
                          },
                          newEquipImageFile || undefined
                        );
                        await queryClient.invalidateQueries({ queryKey: equipmentQueryKey });
                        await queryClient.invalidateQueries({ queryKey: queryKeys.equipment.options });
                        toast({ title: "Equipment created" });
                      }
                    } catch (error: any) {
                      const errorMsg = error?.message || String(error);
                      console.error("Equipment save error:", error);
                      if (errorMsg.includes("already exists")) {
                        setEquipmentError(`Equipment with name "${trimmedName}" already exists`);
                        toast({ title: "Duplicate name", description: `An equipment item with the name "${trimmedName}" already exists. Please choose a different name.`, variant: "destructive" as any });
                      } else {
                        setEquipmentError(errorMsg);
                        toast({ title: editingEquipId ? "Failed to update" : "Failed to create", description: errorMsg, variant: "destructive" as any });
                      }
                      // Don't close dialog or reset form on error - let user see the error and try again
                      return;
                    }
                    // Only reset and close on success
                    setNewEquipName("");
                    setNewEquipDescription("");
                    setNewEquipInstructions("");
                    setNewEquipAffiliateUrl("");
                    setNewEquipImagePreview(null);
                    setNewEquipImageFile(null);
                    setNewEquipIsStandard(false);
                    setEditingEquipId(null);
                    setEquipmentError(null);
                    setEquipDialogOpen(false);
                  }}>Save</Button>
                </DialogFooter>
              </DialogScrollContent>
            </Dialog>

            <AlertDialog open={Boolean(equipToDeleteId)} onOpenChange={(open) => !open && setEquipToDeleteId(null)}>
              <AlertDialogContent className="bg-white text-black">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Equipment?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will soft-delete the equipment. You can reactivate it later from the Deleted Equipment tab.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={async () => {
                    if (!equipToDeleteId) return;
                    try {
                      await softDeleteEquipment(equipToDeleteId);
                      await queryClient.invalidateQueries({ queryKey: equipmentQueryKey });
                      await queryClient.invalidateQueries({ queryKey: queryKeys.equipment.options });
                      toast({ title: "Equipment deleted" });
                    } catch (error: any) {
                      toast({ title: "Failed to delete", description: error.message, variant: "destructive" as any });
                    }
                    setEquipToDeleteId(null);
                  }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        );
      case "exercises":
        return <AdminExercisesView />;
      case "workouts":
        // In workouts tab, we're editing templates, not assigned workouts
        // So we should pass null for assignmentContext
        // workoutAssignmentContext is only used in clients tab for assigned workouts
        return <WorkoutsView 
          assignmentContext={null}
          onClearAssignmentContext={() => {
            // This shouldn't be called in workouts tab since assignmentContext is null
            // But keeping for safety
            setWorkoutAssignmentContext(null);
          }}
        />;
      case "lessons":
        return <LessonsView />;
      case "contacts":
        return <ContactSubmissionsView />;
      case "programs":
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Program Templates</h1>
                <p className="text-gray-600">General program templates for all trainers.</p>
              </div>
              <Button 
                onClick={() => openProgramDialogFor()}
                className="bg-gradient-to-r from-primary to-lime-hover"
              >
                Create Program
              </Button>
            </div>

            {programsLoading ? (
              <div className="text-center py-8 text-gray-600">Loading programs...</div>
            ) : programs.length === 0 ? (
              <div className="text-center py-8 text-gray-600">No programs found. Create your first program to get started.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {programs.map((program) => (
                  <Card key={program.id} className="hover:shadow-xl transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-lg line-clamp-2">{program.name}</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">{program.description}</p>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Duration:</span>
                          <span className="font-medium text-gray-800">{program.duration} weeks</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Frequency:</span>
                          <span className="font-medium text-gray-800">{program.frequency}x per week</span>
                        </div>
                        {program.weekly_schedule_preference?.trim() ? (
                          <div className="text-sm">
                            <span className="text-gray-600">Preferred scheduling:</span>
                            <p className="mt-1 text-gray-800">{program.weekly_schedule_preference}</p>
                          </div>
                        ) : null}
                        <div className="flex justify-between text-sm items-center">
                          <span className="text-gray-600">Difficulty:</span>
                          <Badge className={getDifficultyColor(program.difficulty)}>
                            {program.difficulty}
                          </Badge>
                        </div>
                      </div>
                      
                      {program.motivations.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-gray-700 mb-2">Motivations:</p>
                          <div className="flex flex-wrap gap-1">
                            {program.motivations.map((id) => {
                              const motivation = motivationOptions.find(m => m.id === id);
                              return motivation ? (
                                <Badge key={id} variant="secondary" className="text-xs">
                                  {motivation.icon} {motivation.label}
                                </Badge>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                      
                      {program.exercise_types.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-gray-700 mb-2">Exercise Types:</p>
                          <div className="flex flex-wrap gap-1">
                            {program.exercise_types.map((id) => {
                              const label = labels.exerciseType[id] ?? id;
                              return label ? (
                                <Badge key={id} variant="outline" className="text-xs">
                                  {label}
                                </Badge>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => openProgramDialogFor(program)}
                        >
                          <Edit3 className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleDuplicateProgram(program)}
                        >
                          Duplicate
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setDeleteProgramId(program.id);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Program Dialog */}
            <Dialog open={programDialogOpen} onOpenChange={(open) => { 
              setProgramDialogOpen(open); 
              if (!open) { 
                setEditingProgram(null);
                setProgramName("");
                setProgramDescription("");
                setProgramDuration(12);
                setProgramFrequency(3);
                setProgramWeeklySchedulePreference("");
                setProgramDifficulty("Beginner");
                setProgramMotivations([]);
                setProgramExerciseTypes([]);
                setProgramFocus("");
                setProgramWhatToExpect("");
                setProgramVariety("");
              } 
            }}>
              <DialogScrollContent className="bg-white text-black sm:max-w-[800px]">
                <DialogHeader className="shrink-0">
                  <DialogTitle>{editingProgram ? "Edit Program" : "Create Program"}</DialogTitle>
                  <DialogDescription>Fill out the program details below.</DialogDescription>
                </DialogHeader>
                <DialogBody className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="program-name">Name *</Label>
                    <Input 
                      id="program-name" 
                      value={programName} 
                      onChange={(e) => setProgramName(e.target.value)} 
                      placeholder="e.g., 5/3/1 Powerlifting Program" 
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="program-description">Description *</Label>
                    <Textarea 
                      id="program-description" 
                      value={programDescription} 
                      onChange={(e) => setProgramDescription(e.target.value)} 
                      placeholder="Describe the program goals and approach"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="program-duration">Duration (weeks) *</Label>
                      <Input 
                        id="program-duration" 
                        type="number" 
                        min="1"
                        value={programDuration} 
                        onChange={(e) => setProgramDuration(parseInt(e.target.value) || 0)} 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="program-frequency">Frequency (sessions/week) *</Label>
                      <Input 
                        id="program-frequency" 
                        type="number" 
                        min="1"
                        value={programFrequency} 
                        onChange={(e) => setProgramFrequency(parseInt(e.target.value) || 0)} 
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="program-weekly-schedule-preference">
                      Preferred Workout Scheduling Across the Week
                    </Label>
                    <Textarea
                      id="program-weekly-schedule-preference"
                      value={programWeeklySchedulePreference}
                      onChange={(e) => setProgramWeeklySchedulePreference(e.target.value)}
                      placeholder="Optional: e.g., lift Mon/Wed/Fri, conditioning Tue/Sat"
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="program-difficulty">Difficulty</Label>
                    <Select value={programDifficulty} onValueChange={(value) => setProgramDifficulty(value as DifficultyEnum)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select difficulty level" />
                      </SelectTrigger>
                      <SelectContent>
                        {(DIFFICULTY as unknown as string[]).map((level) => (
                          <SelectItem key={level} value={level}>{level}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Motivations</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border rounded-lg">
                      {motivationOptions.map((motivation) => (
                        <div key={motivation.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`motivation-${motivation.id}`}
                            checked={programMotivations.includes(motivation.id)}
                            onCheckedChange={() => toggleMotivation(motivation.id)}
                          />
                          <Label 
                            htmlFor={`motivation-${motivation.id}`}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {motivation.icon} {motivation.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {programMotivations.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {programMotivations.map((id) => {
                          const motivation = motivationOptions.find(m => m.id === id);
                          return motivation ? (
                            <Badge key={id} variant="secondary" className="text-xs">
                              {motivation.icon} {motivation.label}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Exercise Types</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border rounded-lg">
                      {programExerciseTypeOptions.map((exerciseType) => (
                        <div key={exerciseType.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`exercise-type-${exerciseType.id}`}
                            checked={programExerciseTypes.includes(exerciseType.id)}
                            onCheckedChange={() => toggleExerciseType(exerciseType.id)}
                          />
                          <Label 
                            htmlFor={`exercise-type-${exerciseType.id}`}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {exerciseType.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {programExerciseTypes.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {programExerciseTypes.map((id) => {
                          const label = labels.exerciseType[id] ?? id;
                          return label ? (
                            <Badge key={id} variant="outline" className="text-xs">
                              {label}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>

                  {programExerciseTypes.length > 0 && (
                    <div className="space-y-3">
                      <Label>Exercise Type Details</Label>
                      <div className="space-y-3">
                        {programExerciseTypes.map((id) => {
                          const label = labels.exerciseType[id] ?? id;
                          return (
                            <div key={id} className="space-y-2">
                              <Label htmlFor={`exercise-type-detail-${id}`} className="text-sm font-medium">
                                {label}
                              </Label>
                              <Textarea
                                id={`exercise-type-detail-${id}`}
                                value={programExerciseTypeDetails[id] ?? ""}
                                onChange={(e) =>
                                  setProgramExerciseTypeDetails((prev) => ({
                                    ...prev,
                                    [id]: e.target.value,
                                  }))
                                }
                                placeholder={`Add details for ${label} (optional)`}
                                rows={2}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="program-focus">Program Focus</Label>
                    <Textarea 
                      id="program-focus" 
                      value={programFocus} 
                      onChange={(e) => setProgramFocus(e.target.value)} 
                      placeholder="What is the main focus of this program?"
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="program-what-to-expect">What to Expect</Label>
                    <Textarea 
                      id="program-what-to-expect" 
                      value={programWhatToExpect} 
                      onChange={(e) => setProgramWhatToExpect(e.target.value)} 
                      placeholder="What can users expect from this program?"
                      rows={2}
                    />
                  </div>

                  <VarietyField
                    id="program-variety"
                    value={programVariety}
                    onChange={setProgramVariety}
                  />
                </DialogBody>
                <DialogFooter className="shrink-0">
                  <Button 
                    variant="outline" 
                    className="border-gray-300 text-gray-800 hover:bg-gray-100 hover:text-gray-900" 
                    onClick={() => setProgramDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={saveProgram}>
                    {editingProgram ? "Save Changes" : "Create Program"}
                  </Button>
                </DialogFooter>
              </DialogScrollContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <AlertDialogContent className="bg-white text-black">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Program</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this program? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => {
                    setDeleteDialogOpen(false);
                    setDeleteProgramId(null);
                  }}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteProgram}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        );
      case "clients":
        // If we have workoutAssignmentContext, render WorkoutsView instead
        // This allows us to keep "clients" highlighted in sidebar while showing workout canvas
        if (workoutAssignmentContext) {
          return <WorkoutsView 
            key={`workout-assignment-${workoutAssignmentContext.clientId}-${workoutAssignmentContext.assignedWorkoutId || 'new'}`}
            assignmentContext={workoutAssignmentContext}
            onClearAssignmentContext={() => {
              setWorkoutAssignmentContext(null);
              // Restore client view from URL or localStorage (for migration)
              const clientId = workoutAssignmentContext?.backToClientId || searchParams.get("clientId") || localStorage.getItem("admin-viewing-client-id");
              if (clientId) {
                setViewingClientId(clientId);
                // Always set to workouts tab with scheduled view when coming back from workout assignment
                setClientProfileTab("workouts");
                setClientWorkoutView("scheduled");
                // Clean up localStorage (migration cleanup)
                localStorage.removeItem("admin-viewing-client-id");
                localStorage.removeItem("admin-client-profile-tab");
                localStorage.removeItem("admin-client-workout-view");
              }
            }}
          />;
        }
        return <AdminClientsView 
          workoutAssignmentContext={workoutAssignmentContext}
          setWorkoutAssignmentContext={setWorkoutAssignmentContext}
          setActiveTab={setActiveTab}
          viewingClientId={viewingClientId}
          setViewingClientId={setViewingClientId}
          clientProfileTab={clientProfileTab}
          setClientProfileTab={setClientProfileTab}
          clientWorkoutView={clientWorkoutView}
          setClientWorkoutView={setClientWorkoutView}
        />;
      // Removed: prompts, legal, pricing, faqs (now inside Settings)
      case "notifications":
        return <NotificationsView />;
      case "users":
        return <UsersView />;
      case "settings":
        return <SettingsView />;
      case "profile":
        return <ProfileSettingsView />;
    }
  };

  const setTab = (id: typeof sidebarItems[number]["id"]) => {
    setActiveTab(id as any);
    // URL will be updated by the useEffect hook
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-gray-50 admin-portal">
        <Sidebar className="gradient-dark text-white border-r border-white/10 [&_[data-sidebar=sidebar]]:bg-transparent">
          <SidebarHeader className="px-6 pt-8 pb-8 border-b border-white/10">
            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={() => setTab(sidebarItems[0].id)}
                className="w-full transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded"
                aria-label="Go to the first admin tab"
              >
                <img
                  src={frequentHealthLogo}
                  alt="Frequent Health"
                  className="w-full h-auto object-contain"
                />
              </button>
            </div>
          </SidebarHeader>
          <SidebarContent className="px-4 pt-8">
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu className="gap-1.5">
                  {sidebarItems.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        onClick={() => setTab(item.id as any)}
                        isActive={activeTab === item.id}
                        className="w-full justify-start rounded-lg text-white/80 font-inter font-medium hover:bg-white/10 hover:text-white transition-all duration-200 data-[active=true]:bg-primary data-[active=true]:text-black"
                      >
                        <item.icon className="w-5 h-5" />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter className="mt-auto px-5 py-4 border-t border-white/10">
            <button
              onClick={() => setTab("profile")}
              className="flex items-center gap-3 bg-white/5 hover:bg-white/10 rounded-xl p-3 w-full transition-colors"
            >
              <Avatar className="h-9 w-9">
                <AvatarImage
                  src={resolveBackendAssetUrl(profile?.avatar_url) || undefined}
                  alt={`${profile?.first_name} ${profile?.last_name}`}
                />
                <AvatarFallback className="bg-primary text-black">
                  {profile?.first_name?.[0]}{profile?.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 text-left">
                <p className="text-sm font-medium truncate text-white">
                  {profile?.first_name} {profile?.last_name}
                </p>
                <p className="text-xs text-white/70 truncate">
                  {isAdmin ? 'Admin' : isTrainer ? 'Trainer' : 'User'}
                </p>
              </div>
            </button>
          </SidebarFooter>
        </Sidebar>

        <main className="flex-1">
          <div className="flex items-center justify-between px-4 py-2">
            <SidebarTrigger />
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm">
                <Bell className="w-5 h-5" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="transition-opacity hover:opacity-80 focus:outline-none">
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={resolveBackendAssetUrl(profile?.avatar_url) || undefined}
                        alt={`${profile?.first_name} ${profile?.last_name}`}
                      />
                      <AvatarFallback className="bg-primary text-black">
                        {profile?.first_name?.[0]}{profile?.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white text-black">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {profile?.first_name} {profile?.last_name}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {profile?.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setTab("profile")}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Profile Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {renderTab()}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default AdminPortal;